// @ts-nocheck
// Agent Portal (/cti2) — the whole disposition lifecycle, split out of
// AgentPortalPage.tsx: open (from a ringing/just-ended call), validate,
// submit (live or a reopened Pending item), wrap-up countdown/timeout, and
// the pending-dispositions list. Byte-faithful port of CTIAgentUI.tsx's
// own disposition handling.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCallStore } from '@/router/zustand/callStore';
import { getUser } from '@/utilities/setting';
import { getApiCall, postApiCall, patchApiCall } from '@/service/httpCall';
import { getContactGroupMemberByNumber } from '@/service/contactGroupMember';
import { getAgentMasking, maskNumber } from '@/utilities/cliDisplay';
import { trackActivity } from '@/service/activityTracker';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { toast } from 'sonner';
import moment from 'moment';
import {
  API_DISPOSITION_BY_CONTEXT,
  API_DISPOSITION_FORM_DATA_CREATE,
  API_DISPOSITION_FORM_DATA_UPDATE,
  API_DISPOSITION_PENDING_LIST,
  API_DISPOSITION_PENDING_SAVE,
  API_DISPOSITION_PENDING_RESOLVE,
  API_DISPOSITION_WRAP_UP_START,
  API_DISPOSITION_WRAP_UP_COMPLETE,
  API_DISPOSITION_WRAP_UP_ABANDON,
  API_AGENT_LEADS_COMPLETE,
} from '@/service/url';

// True when `a` and `b` differ by at most one character (insert, delete or
// substitute) — matches "firstame" against the canonical "firstname", a
// typo an admin can easily make while naming a disposition-form field in
// the Disposition Forms builder. The exact-match-only version of this
// check silently failed to recognize the field, so nothing this normally
// syncs to the linked contact record (first/last name) ever went through
// for that field, even though its own answer still saved fine as part of
// the disposition's raw field data.
function isOneEditOrLess(a, b) {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  let i = 0; let j = 0; let foundDiff = false;
  while (i < shorter.length && j < longer.length) {
    if (shorter[i] !== longer[j]) {
      if (foundDiff) return false;
      foundDiff = true;
      if (shorter.length === longer.length) i++;
      j++;
    } else { i++; j++; }
  }
  return true;
}

const normalizeFieldName = (name) => String(name || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
const isContactNumberField = (name) => isOneEditOrLess(normalizeFieldName(name), 'contactnumber');
const isFirstNameField = (name) => isOneEditOrLess(normalizeFieldName(name), 'firstname');
const isLastNameField = (name) => isOneEditOrLess(normalizeFieldName(name), 'lastname');

const LIVE_CALL_STATES = ['incoming', 'dialing', 'oncall', 'connected', 'hold', 'conference'];

function pickResumableWrapUp(list) {
  const now = Date.now();
  for (const row of list || []) {
    if (!row?.context?.meta || !row?.expires_at) continue;
    const seconds = Number(row?.wrap_up_seconds);
    if (!Number.isFinite(seconds) || seconds <= 0) continue;
    const expiresAt = new Date(row.expires_at).getTime();
    if (Number.isNaN(expiresAt) || expiresAt - now < 1000) continue;
    return { row, deadline: expiresAt };
  }
  return null;
}

const getCompletedCallSeconds = (call) => {
  if (!call) return null;
  // Prefer the stored total_duration (call duration); billing/patch/timestamps are fallbacks.
  if (call.total_duration != null && call.total_duration !== '') {
    const total = Number(call.total_duration);
    if (Number.isFinite(total) && total >= 0) return Math.round(total);
  }
  const billing = Number(call.billing_duration ?? call.local_talk_seconds);
  if (Number.isFinite(billing) && billing >= 0) return Math.round(billing);
  const patch = Number(call.patch_duration);
  if (Number.isFinite(patch) && patch >= 0) return Math.round(patch);
  const toMs = (v) => { if (!v) return null; const ms = new Date(v).getTime(); return Number.isNaN(ms) ? null : ms; };
  const bridge = toMs(call.bridge_time);
  const end = toMs(call.end_time);
  if (bridge != null && end != null && end >= bridge) return Math.round((end - bridge) / 1000);
  const start = toMs(call.start_time);
  if (start != null && end != null && end >= start) return Math.round((end - start) / 1000);
  return null;
};

function buildDispositionPayload(meta, values, userData) {
  const fields = meta?.fields || [];
  const notesText = String(values?.remarks || '').trim();
  const baseStatus = Array.isArray(values?.remarks_status) ? values.remarks_status : [];
  const remarksStatus = notesText ? [...baseStatus, notesText] : baseStatus;
  let typedData: any = {};
  let contactFirstName = ''; let contactLastName = '';
  fields.forEach((f) => {
    const name = String(f?.field_name || '').trim();
    if (!name) return;
    const rawVal = values?.[name];
    const type = f?.field_type || 'String';
    // Boolean/Multi Select always get a key — even when "empty" (an
    // unchecked box, or nothing picked) — same as the empty-string case
    // right below: previously an empty/false value was skipped entirely,
    // so the field just never appeared in the payload. If the backend
    // treats a missing key as "leave whatever was saved before alone"
    // rather than "clear it", removing a previously-saved optional
    // value never actually cleared it in the DB.
    if (type === 'Boolean') { typedData[name] = Boolean(rawVal); return; }
    if (type === 'Multi Select') {
      typedData[name] = Array.isArray(rawVal) ? rawVal : (rawVal ? [rawVal] : []);
      return;
    }
    const isEmpty = rawVal === null || rawVal === undefined || String(rawVal).trim() === '';
    if (isEmpty) { typedData[name] = ''; return; }
    // Previously fell through to `else typedData[name] = String(rawVal)`
    // for EVERY type once past the empty-check, including Boolean — that
    // stringified `true`/`false` into the literal text "true"/"false"
    // instead of a real boolean, breaking anything downstream (CC Admin
    // reports, re-loading this data as a form) that expects an actual
    // boolean there.
    if (type === 'Integer') typedData[name] = parseInt(rawVal, 10);
    else if (type === 'Float') typedData[name] = Number(rawVal);
    else typedData[name] = String(rawVal);
  });
  typedData.remarks_status = remarksStatus;
  fields.forEach((f) => {
    const v = values?.[f?.field_name];
    if (isFirstNameField(f?.field_name) && v) contactFirstName = String(v);
    else if (isLastNameField(f?.field_name) && v) contactLastName = String(v);
  });
  const contactNumber = meta?.callerNumber || values?.contact_number || null;
  const payload: any = {
    campaign_id: meta?.campaign_id, agent_id: userData?.agentData?.id,
    disposition_form_id: meta?.disposition_form_id || null, disposition_source: meta?.disposition_source || null,
    call_sid: meta?.call_sid || null, data: typedData,
    contact: { primary_number: contactNumber, first_name: contactFirstName, last_name: contactLastName },
  };
  if (meta?.dispositionForm?.follow_ups_enabled) {
    payload.follow_up = { enabled: Boolean(values?.follow_up_enabled), date: values?.follow_up_date || null, time: values?.follow_up_time || null };
  }
  return { payload, typedData };
}

// "Follow-up scheduled with Priya Sharma on 14 Oct 2026 at 5:30 PM." — contact
// name from the form when given, else the number (masked when the agent's is).
function followUpScheduledLine(followUp, contact) {
  const name = [contact?.first_name, contact?.last_name].map((v) => String(v || '').trim()).filter(Boolean).join(' ');
  const number = followUp?.did_number || contact?.primary_number;
  const who = name || (number ? (getAgentMasking(getUser()) ? maskNumber(number) : number) : 'this contact');
  const at = moment(`${String(followUp?.follow_up_date || '').slice(0, 10)} ${String(followUp?.follow_up_time || '').slice(0, 5)}`, 'YYYY-MM-DD HH:mm', true);
  return at.isValid() ? `Follow-up scheduled with ${who} on ${at.format('DD MMM YYYY')} at ${at.format('h:mm A')}.` : `Follow-up scheduled with ${who}.`;
}

function finalizeDispositionSubmit({ meta, typedData, contact, createResp, completeAgentId, source }) {
  if (meta?.is_lead && meta?.lead_item_id) {
    postApiCall(API_AGENT_LEADS_COMPLETE.replace(':itemId', meta.lead_item_id), { data: typedData })
      .catch((e) => console.error('Failed to mark lead complete', e))
      .finally(() => useCallStore.getState().bumpLeadsRefresh());
  } else {
    useCallStore.getState().bumpLeadsRefresh();
  }
  if (completeAgentId) {
    postApiCall(API_DISPOSITION_WRAP_UP_COMPLETE, { agent_id: completeAgentId, call_sid: meta?.call_sid || null, disposition_form_data_id: createResp?.disposition_form_data?.id || null, campaign_id: meta?.campaign_id || null, disposition_source: meta?.disposition_source || null })
      .catch((err) => console.error('Failed to record disposition wrap-up', err));
    postApiCall(API_DISPOSITION_PENDING_RESOLVE, { agent_id: completeAgentId, call_sid: meta?.call_sid || null })
      .catch((err) => console.error('Failed to resolve pending disposition', err));
  }
  trackActivity('disposition_submit', { campaign_id: meta?.campaign_id || null, call_sid: meta?.call_sid || null, disposition_form_id: meta?.disposition_form_id || null, disposition_source: meta?.disposition_source || null, remarks_status: typedData?.remarks_status || [], field_count: Object.keys(typedData || {}).length, source });
  const followUp = createResp?.disposition_follow_up;
  if (followUp) {
    useCallStore.getState().bumpFollowUpsRefresh();
    toast.success('Disposition saved', { description: followUpScheduledLine(followUp, contact) });
  } else {
    toast.success('Disposition saved');
  }
}

export function useDispositionFlow({
  userData, loggedUserId, callState, outgoingRingingActive,
  sipCampaignId, sipCallGroupId, sipLeadData, activeCampaignFromSip, callerNumber,
  completedCallToPersist, manualScreen, setManualScreen, screen,
  lastSipCampaignIdRef, lastSipCallGroupIdRef, lastCallerContactRef, lastSipLeadDataRef,
}) {
  const setStoreDispositionSubmitting = useCallStore((s) => s.setDispositionSubmitting);
  const setStoreDispositionOpen = useCallStore((s) => s.setDispositionOpen);
  const setStoreDispositionActive = useCallStore((s) => s.setDispositionActive);

  const [dispositionSubmitting, setDispositionSubmitting] = useState(false);
  const [dispositionValues, setDispositionValues] = useState<any>({});
  const [dispositionErrors, setDispositionErrors] = useState<any>({});
  const [dispositionMeta, setDispositionMeta] = useState<any>(null);
  // Kept only long enough to render a compact read-only summary if the
  // agent is still on the same call after submitting (dispositionMeta/
  // dispositionValues both clear immediately on success) — otherwise
  // DispositionCard fell back to its "Details" field grid completely
  // empty, inviting a second, blank disposition for the call just saved.
  const [lastSubmittedDisposition, setLastSubmittedDisposition] = useState<any>(null);
  const [editingSubmitted, setEditingSubmitted] = useState(false);
  const [dispositionOpenedForCallSid, setDispositionOpenedForCallSid] = useState<any>(null);
  const [dispositionDeadline, setDispositionDeadline] = useState<any>(null);
  const [dispositionSecondsLeft, setDispositionSecondsLeft] = useState<any>(null);
  const [pendingDispositions, setPendingDispositions] = useState<any[]>([]);

  const dispositionFetchInFlightForCallSidRef = useRef<any>(null);
  const dispositionSubmittedForCallSidRef = useRef<any>(null);
  const dispositionTimedOutForCallSidRef = useRef<any>(null);
  const dispositionPendingArmRef = useRef<any>(null);
  const wrapUpActiveRef = useRef(false);
  const wrapUpAgentIdRef = useRef<any>(null);
  const wrapUpResumeCheckedRef = useRef(false);
  const activeDispositionSidRef = useRef<any>(null);
  const pendingSavedSidRef = useRef<any>(null);
  const dispositionSubmittedDuringCallRef = useRef(false);

  const openDispositionForCall = useCallback(async ({ callSid, campaignId, callGroupId, direction, callerNumberRaw, didNumberRaw, calleeNumberRaw, contact, leadData, activeCampaign, afterCall = false, leadContext = null, callDurationSeconds = null }: any) => {
    if (getUser()?.agentData?.access === 'team_lead') return;
    wrapUpResumeCheckedRef.current = true;
    if (!afterCall) dispositionSubmittedDuringCallRef.current = false;
    dispositionFetchInFlightForCallSidRef.current = callSid || 'no-sid';
    let dispositionForm: any = null;
    let source: any = null;
    if (leadContext?.disposition_form) { dispositionForm = leadContext.disposition_form; source = 'lead'; }
    else {
      try {
        const params: any = {};
        if (campaignId) params.campaign_id = campaignId;
        if (callGroupId) params.call_group_id = callGroupId;
        if (direction) params.call_direction = direction;
        const resp: any = await getApiCall(API_DISPOSITION_BY_CONTEXT, params, false);
        dispositionForm = resp?.disposition_form || null;
        source = resp?.source || null;
      } catch (err) { console.error('Failed to load disposition form by context', err); dispositionFetchInFlightForCallSidRef.current = null; return; }
    }
    if (!dispositionForm) {
      dispositionFetchInFlightForCallSidRef.current = null;
      // No form for this call: drop the previous call's "Disposition saved"
      // card so it doesn't look like this call has a disposition.
      setLastSubmittedDisposition((prev: any) => (prev && prev.callSid !== (callSid || null) ? null : prev));
      setEditingSubmitted(false);
      if (!activeDispositionSidRef.current) { setDispositionValues({}); setDispositionErrors({}); }
      return;
    }
    const dispositionFormId = dispositionForm?.id || null;
    const effectiveWrapUpSeconds = (() => { const n = Number(dispositionForm?.effective_wrap_up_seconds); return Number.isFinite(n) && n > 0 ? Math.round(n) : null; })();
    const rawFields = dispositionForm?.fields;
    const fields = Array.isArray(rawFields) ? rawFields : typeof rawFields === 'string' ? (() => { try { const p = JSON.parse(rawFields); return Array.isArray(p) ? p : []; } catch { return []; } })() : [];

    const lastTen = (value) => { if (!value) return value; const digits = String(value).replace(/\D/g, ''); return digits.length > 10 ? digits.slice(-10) : digits || value; };
    // The numbers above were captured before the disposition-form fetch;
    // on an outbound call the dialed number often lands in the store only
    // after that, which left Contact number blank. Re-read it live.
    const liveDialedNumber = (() => {
      if (direction !== 'outbound') return null;
      const s: any = useCallStore.getState();
      const domValue = typeof document !== 'undefined'
        ? (document.getElementById('numberToCall') as HTMLInputElement | null)?.value
        : '';
      return s.activeCallMeta?.callee_number || s.activeCallMeta?.customer_number || s.callerNumber || domValue || null;
    })();
    const dispositionContactNumber = direction === 'outbound' ? (calleeNumberRaw || liveDialedNumber || callerNumberRaw) : lastTen(callerNumberRaw);

    let resolvedContact: any = contact || useCallStore.getState().callerContact || lastCallerContactRef.current || null;
    if (!resolvedContact && dispositionContactNumber) { try { const fetched = await getContactGroupMemberByNumber(lastTen(dispositionContactNumber)); if (fetched) resolvedContact = fetched; } catch { } }

    const leadFullName = leadContext?.contact_name ? String(leadContext.contact_name).trim() : '';
    let leadFirstName = ''; let leadLastName = '';
    if (leadFullName) { const parts = leadFullName.split(/\s+/); leadFirstName = parts.shift() || ''; leadLastName = parts.join(' '); }

    const storedDetails = resolvedContact?.additional_details && typeof resolvedContact.additional_details === 'object' && !Array.isArray(resolvedContact.additional_details) ? resolvedContact.additional_details : {};
    const storedDetailByNormalizedKey = new Map(Object.entries(storedDetails).map(([k, v]) => [normalizeFieldName(k), v]));
    const storedDetailFor = (key) => { const v = storedDetailByNormalizedKey.get(normalizeFieldName(key)); if (v === null || v === undefined || typeof v === 'object') return null; const str = String(v); return str.trim() === '' ? null : str; };

    const initial: any = { remarks_status: [], follow_up_enabled: false, follow_up_date: '', follow_up_time: '' };
    fields.forEach((f) => {
      const key = String(f?.field_name || '').trim();
      if (!key) return;
      let val = f?.field_default;
      if (val === null || val === undefined) val = '';
      if (f?.field_type === 'Date' && val) { const d = new Date(val); if (!Number.isNaN(d.getTime())) val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
      if (isContactNumberField(key) && dispositionContactNumber) val = dispositionContactNumber;
      if (isFirstNameField(key)) { if (leadFirstName) val = leadFirstName; else if (resolvedContact?.first_name) val = resolvedContact.first_name; }
      if (isLastNameField(key)) { if (leadLastName) val = leadLastName; else if (resolvedContact?.last_name) val = resolvedContact.last_name; }
      else if (!isContactNumberField(key) && !isFirstNameField(key)) { const stored = storedDetailFor(key); if (stored !== null) val = stored; }
      initial[key] = String(val);
    });

    setDispositionErrors({});
    setDispositionValues(initial);

    const metaObj = {
      campaign_id: leadContext ? null : String(campaignId || activeCampaign?.campaign_id || activeCampaign?.id || ''),
      call_group_id: callGroupId ? String(callGroupId) : null,
      agent_id: String(loggedUserId || ''),
      disposition_form_id: dispositionFormId ? String(dispositionFormId) : null,
      disposition_source: source,
      dispositionForm,
      fields, call_sid: callSid,
      campaign_name: String(activeCampaign?.campaign_name || ''),
      callerNumber: dispositionContactNumber,
      call_direction: direction || null,
      wrap_up_seconds: effectiveWrapUpSeconds,
      is_lead: Boolean(leadContext),
      lead_item_id: leadContext?.item_id || null,
      lead_assignment_id: leadContext?.assignment_id || null,
      is_lead_recall: Boolean(leadContext?.is_recall),
      call_duration_seconds: Number.isFinite(callDurationSeconds) && callDurationSeconds >= 0 ? Math.round(callDurationSeconds) : null,
    };
    setDispositionMeta(metaObj);
    setLastSubmittedDisposition(null); setEditingSubmitted(false);
    setDispositionOpenedForCallSid(callSid || 'no-sid');
    dispositionFetchInFlightForCallSidRef.current = null;
    dispositionTimedOutForCallSidRef.current = null;
    wrapUpActiveRef.current = false;
    const wrapUpAgentId = userData?.agentData?.id || null;
    if (wrapUpAgentId) {
      wrapUpAgentIdRef.current = wrapUpAgentId;
      const previousCallSid = afterCall ? pendingSavedSidRef.current : null;
      pendingSavedSidRef.current = callSid || null;
      postApiCall(API_DISPOSITION_PENDING_SAVE, {
        previous_call_sid: previousCallSid,
        agent_id: wrapUpAgentId, call_sid: callSid || null, campaign_id: metaObj.campaign_id || null, call_group_id: metaObj.call_group_id || null,
        disposition_form_id: metaObj.disposition_form_id || null, disposition_source: metaObj.disposition_source || null, call_direction: direction || null,
        wrap_up_seconds: effectiveWrapUpSeconds || null, context: { meta: metaObj, values: initial, wrap_up_seconds: effectiveWrapUpSeconds || null },
      }).catch((err) => console.error('Failed to persist pending disposition', err));
    }
    if (effectiveWrapUpSeconds && effectiveWrapUpSeconds > 0) {
      // A call that dropped while the form was still being fetched has
      // already gone idle — the arm effect below ran then with nothing to
      // arm, and won't run again (refs don't re-trigger it), so the timer
      // sat at its full value forever. Start the countdown right away.
      const s: any = useCallStore.getState();
      const callStillLive = LIVE_CALL_STATES.includes(s.callState) || s.outgoingRingingActive;
      if (afterCall || !callStillLive) { dispositionPendingArmRef.current = null; setDispositionDeadline(Date.now() + effectiveWrapUpSeconds * 1000); }
      else { dispositionPendingArmRef.current = { callSid: callSid || 'no-sid', seconds: effectiveWrapUpSeconds }; setDispositionDeadline(null); setDispositionSecondsLeft(effectiveWrapUpSeconds); }
    } else { dispositionPendingArmRef.current = null; setDispositionDeadline(null); }
  }, [loggedUserId, userData, lastCallerContactRef]);

  useEffect(() => {
    const isRinging = callState === 'incoming' || outgoingRingingActive;
    if (!isRinging) return;
    const store = useCallStore.getState();
    const liveCallSid = store.activeCallMeta?.call_sid || store.sipHeaders?.['call-id'] || null;
    const sidKey = liveCallSid || 'no-sid';
    if (sidKey === dispositionOpenedForCallSid || sidKey === dispositionSubmittedForCallSidRef.current || sidKey === dispositionFetchInFlightForCallSidRef.current) return;
    const rawDirection = store.direction || null;
    const normalizedDirection = rawDirection ? (['incoming', 'inbound', 'in'].includes(String(rawDirection).toLowerCase()) ? 'inbound' : ['outgoing', 'outbound', 'out'].includes(String(rawDirection).toLowerCase()) ? 'outbound' : null) : null;
    openDispositionForCall({
      callSid: liveCallSid,
      campaignId: sipCampaignId || lastSipCampaignIdRef.current || store.activeCallMeta?.campaign_id || store.activeCallMeta?.campaignId || null,
      callGroupId: sipCallGroupId || lastSipCallGroupIdRef.current || store.activeCallMeta?.call_group_id || store.activeCallMeta?.callGroupId || null,
      direction: normalizedDirection,
      callerNumberRaw: store.activeCallMeta?.customer_number || store.sipHeaders?.['x-ns-customernumber'] || store.activeCallMeta?.caller_number || store.callerNumber || null,
      didNumberRaw: store.activeCallMeta?.caller_number || store.callerNumber || null,
      calleeNumberRaw: store.activeCallMeta?.callee_number || null,
      contact: store.callerContact || lastCallerContactRef.current || null,
      leadData: sipLeadData || lastSipLeadDataRef.current,
      activeCampaign: activeCampaignFromSip, afterCall: false, leadContext: store.activeLeadContext || null,
    });
  }, [callState, outgoingRingingActive, dispositionOpenedForCallSid, openDispositionForCall, sipCampaignId, sipCallGroupId, sipLeadData, activeCampaignFromSip, lastSipCampaignIdRef, lastSipCallGroupIdRef, lastCallerContactRef, lastSipLeadDataRef]);

  useEffect(() => {
    if (!completedCallToPersist) return;
    const answerStatus = completedCallToPersist.answer_status || '';
    const callSid = completedCallToPersist.call_sid || null;
    const sidKey = callSid || 'no-sid';
    if (answerStatus !== 'answered') return;
    if (dispositionSubmittedDuringCallRef.current) return;
    if (sidKey === dispositionSubmittedForCallSidRef.current) return;
    if (sidKey === dispositionOpenedForCallSid || sidKey === dispositionFetchInFlightForCallSidRef.current) return;
    const effectiveCampaignId = sipCampaignId || lastSipCampaignIdRef.current || completedCallToPersist?.campaign_id || null;
    const effectiveCallGroupId = sipCallGroupId || lastSipCallGroupIdRef.current || completedCallToPersist?.call_group_id || null;
    const rawDirection = completedCallToPersist?.direction || useCallStore.getState().direction || null;
    const normalizedDirection = rawDirection ? (['incoming', 'inbound', 'in'].includes(String(rawDirection).toLowerCase()) ? 'inbound' : ['outgoing', 'outbound', 'out'].includes(String(rawDirection).toLowerCase()) ? 'outbound' : null) : null;
    openDispositionForCall({
      callSid, campaignId: effectiveCampaignId, callGroupId: effectiveCallGroupId, direction: normalizedDirection,
      callerNumberRaw: completedCallToPersist?.customer_number || completedCallToPersist?.caller_number || callerNumber || null,
      didNumberRaw: completedCallToPersist?.caller_number || callerNumber || null,
      calleeNumberRaw: completedCallToPersist?.callee_number || null,
      contact: useCallStore.getState().callerContact || lastCallerContactRef.current || null,
      leadData: sipLeadData || lastSipLeadDataRef.current,
      activeCampaign: activeCampaignFromSip, afterCall: true, leadContext: useCallStore.getState().activeLeadContext || null,
      callDurationSeconds: getCompletedCallSeconds(completedCallToPersist),
    });
  }, [completedCallToPersist, dispositionOpenedForCallSid, openDispositionForCall, sipCampaignId, sipCallGroupId, sipLeadData, activeCampaignFromSip, callerNumber, lastSipCampaignIdRef, lastSipCallGroupIdRef, lastCallerContactRef, lastSipLeadDataRef]);

  useEffect(() => {
    activeDispositionSidRef.current = dispositionMeta ? (dispositionMeta.call_sid || 'no-sid') : null;
  }, [dispositionMeta]);

  // Outbound disposition opened before the dialed number reached the store:
  // fill the (read-only) contact number once it arrives instead of leaving
  // it blank for the rest of the call.
  // Inbound: same for an 'Unknown' caller later corrected by the socket
  // customerNumber — only while that same call is live.
  const liveCalleeNumber = useCallStore((s: any) => s.activeCallMeta?.callee_number || s.activeCallMeta?.customer_number || s.callerNumber || null);
  const liveInboundNumber = useCallStore((s: any) => s.activeCallMeta?.customer_number || null);
  const liveCallSid = useCallStore((s: any) => s.activeCallMeta?.call_sid || null);
  useEffect(() => {
    if (!dispositionMeta) return;
    const outbound = dispositionMeta.call_direction === 'outbound';
    if (!outbound && (!liveCallSid || liveCallSid !== dispositionMeta.call_sid)) return;
    const hasDigits = (v: any) => /\d/.test(String(v || ''));
    const live = outbound ? liveCalleeNumber : liveInboundNumber;
    if (outbound ? dispositionMeta.callerNumber : hasDigits(dispositionMeta.callerNumber)) return;
    if (!hasDigits(live)) return;
    const number = outbound ? String(live) : String(live).replace(/\D/g, '').slice(-10);
    const isEmpty = (v: any) => (outbound ? !v : !hasDigits(v));
    setDispositionMeta((prev: any) => (prev && isEmpty(prev.callerNumber) ? { ...prev, callerNumber: number } : prev));
    setDispositionValues((prev: any) => {
      const next = { ...(prev || {}) };
      const fields = Array.isArray(dispositionMeta.fields) ? dispositionMeta.fields : [];
      fields.forEach((f: any) => {
        const key = String(f?.field_name || '').trim();
        if (key && isContactNumberField(key) && isEmpty(next[key])) next[key] = number;
      });
      return next;
    });
  }, [dispositionMeta, liveCalleeNumber, liveInboundNumber, liveCallSid]);

  useEffect(() => {
    if (callState !== 'idle' || !dispositionMeta) return;
    if ((dispositionMeta?.call_sid || 'no-sid') === dispositionSubmittedForCallSidRef.current) return;
    setManualScreen('disposition');
  }, [callState, dispositionMeta, setManualScreen]);

  useEffect(() => {
    const pending = dispositionPendingArmRef.current;
    if (!pending) return;
    const callLive = LIVE_CALL_STATES.includes(callState) || outgoingRingingActive;
    if (callLive) return;
    dispositionPendingArmRef.current = null;
    setDispositionDeadline(Date.now() + pending.seconds * 1000);
  }, [callState, outgoingRingingActive]);

  useEffect(() => {
    if (manualScreen !== 'disposition' || wrapUpActiveRef.current || !dispositionDeadline) return;
    const callLive = LIVE_CALL_STATES.includes(callState);
    if (callLive) return;
    const wrapUpAgentId = userData?.agentData?.id || null;
    if (!wrapUpAgentId) return;
    wrapUpActiveRef.current = true;
    wrapUpAgentIdRef.current = wrapUpAgentId;
    postApiCall(API_DISPOSITION_WRAP_UP_START, { agent_id: wrapUpAgentId, call_sid: dispositionMeta?.call_sid || null })
      .catch((err) => console.error('Failed to start disposition wrap-up', err));
  }, [manualScreen, userData, dispositionMeta, callState, dispositionDeadline]);

  const abandonWrapUp = useCallback(() => {
    if (!wrapUpActiveRef.current) return;
    const agentId = wrapUpAgentIdRef.current;
    wrapUpActiveRef.current = false; wrapUpAgentIdRef.current = null;
    if (!agentId) return;
    postApiCall(API_DISPOSITION_WRAP_UP_ABANDON, { agent_id: agentId }).catch((err) => console.error('Failed to reset disposition wrap-up', err));
  }, []);
  useEffect(() => () => abandonWrapUp(), [abandonWrapUp]);

  const resumeWrapUp = useCallback(({ row, deadline }, agentId) => {
    if (getUser()?.agentData?.access === 'team_lead') return;
    const meta = row?.context?.meta;
    if (!meta) return;
    setDispositionErrors({});
    setDispositionValues(row?.context?.values || {});
    setDispositionMeta(meta);
    setLastSubmittedDisposition(null); setEditingSubmitted(false);
    setDispositionOpenedForCallSid(meta?.call_sid || row?.call_sid || 'no-sid');
    activeDispositionSidRef.current = meta?.call_sid || row?.call_sid || 'no-sid';
    dispositionFetchInFlightForCallSidRef.current = null;
    dispositionTimedOutForCallSidRef.current = null;
    dispositionPendingArmRef.current = null;
    wrapUpActiveRef.current = false;
    wrapUpAgentIdRef.current = agentId;
    setDispositionDeadline(deadline);
  }, []);

  const refreshPendingDispositions = useCallback(async () => {
    const agentId = userData?.agentData?.id || null;
    if (!agentId) return;
    try {
      const resp: any = await getApiCall(`${API_DISPOSITION_PENDING_LIST}?agent_id=${agentId}`, {}, false);
      const list = Array.isArray(resp?.pending_dispositions) ? resp.pending_dispositions : [];
      const callLive = LIVE_CALL_STATES.includes(useCallStore.getState().callState);
      const resumable = wrapUpResumeCheckedRef.current || callLive ? null : pickResumableWrapUp(list);
      wrapUpResumeCheckedRef.current = true;
      const openSid = resumable
        ? (resumable.row?.call_sid || 'no-sid')
        : activeDispositionSidRef.current;
      setPendingDispositions(openSid ? list.filter((p) => (p?.call_sid || 'no-sid') !== openSid) : list);
      if (resumable) resumeWrapUp(resumable, agentId);
    } catch (err) { console.error('Failed to load pending dispositions', err); }
  }, [userData, resumeWrapUp]);
  useEffect(() => { refreshPendingDispositions(); }, [refreshPendingDispositions]);

  const handleDispositionTimeout = useCallback(() => {
    const sidKey = dispositionMeta?.call_sid || 'no-sid';
    if (sidKey === dispositionSubmittedForCallSidRef.current) { setDispositionDeadline(null); setDispositionSecondsLeft(null); return; }
    dispositionTimedOutForCallSidRef.current = sidKey;
    dispositionPendingArmRef.current = null;
    abandonWrapUp();
    setDispositionDeadline(null); setDispositionSecondsLeft(null);
    setDispositionMeta(null); setDispositionValues({}); setDispositionErrors({});
    setManualScreen(null);
    activeDispositionSidRef.current = null;
    refreshPendingDispositions();
  }, [abandonWrapUp, refreshPendingDispositions, dispositionMeta, setManualScreen]);

  useEffect(() => {
    if (!dispositionDeadline) { const pending = dispositionPendingArmRef.current; setDispositionSecondsLeft(pending ? pending.seconds : null); return undefined; }
    let firedOut = false;
    const tick = () => {
      const remainingMs = dispositionDeadline - Date.now();
      setDispositionSecondsLeft(Math.max(0, Math.ceil(remainingMs / 1000)));
      if (remainingMs <= 0 && !firedOut) { firedOut = true; handleDispositionTimeout(); }
    };
    tick();
    const interval = setInterval(tick, 500);
    return () => clearInterval(interval);
  }, [dispositionDeadline, handleDispositionTimeout]);

  const validateDisposition = useCallback((meta = dispositionMeta) => {
    const fields = meta?.fields || [];
    const nextErrors: any = {};
    if (!dispositionValues?.remarks_status || dispositionValues.remarks_status.length === 0) nextErrors.remarks_status = 'Required';
    if (fields.length) {
      const getNum = (val) => { if (val === null || val === undefined || val === '') return null; const n = Number(val); return Number.isFinite(n) ? n : null; };
      const normalizeKey = (name) => String(name || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
      fields.forEach((f) => {
        const name = String(f?.field_name || '').trim();
        if (!name) return;
        const value = dispositionValues?.[name];
        const isEmpty = value === null || value === undefined || String(value).trim() === '';
        if (f?.is_required && isEmpty) { nextErrors[name] = 'Required'; return; }
        if ((isFirstNameField(name) || normalizeKey(name) === 'primarynumber') && isEmpty) { nextErrors[name] = 'Required for phonebook'; return; }
        if (String(value || '').trim() === '') return;
        if (f?.field_type === 'Integer' || f?.field_type === 'Float') {
          const n = getNum(value);
          if (n === null) { nextErrors[name] = 'Invalid number'; return; }
          if (f?.field_type === 'Integer' && !Number.isInteger(n)) { nextErrors[name] = 'Must be an integer'; return; }
          const minN = getNum(f?.field_min); const maxN = getNum(f?.field_max);
          if (typeof minN === 'number' && n < minN) { nextErrors[name] = `Min ${minN}`; return; }
          if (typeof maxN === 'number' && n > maxN) { nextErrors[name] = `Max ${maxN}`; return; }
        }
        if (f?.field_type === 'Date') {
          const d = new Date(value);
          if (Number.isNaN(d.getTime())) { nextErrors[name] = 'Invalid date'; return; }
        }
      });
    }
    setDispositionErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [dispositionMeta, dispositionValues]);

  const submitDisposition = useCallback(async () => {
    if (!dispositionMeta) { toast.error('No disposition form for this call'); return { ok: false }; }
    if (dispositionSubmitting || !validateDisposition()) return { ok: false };
    if (!userData?.agentData?.id) { toast.error('Missing agent identifiers'); return { ok: false }; }
    setDispositionSubmitting(true);
    // Snapshotted before the create call — is_lead/lead_item_id/lead_assignment_id
    // get wiped from dispositionMeta (cleared to null) once this succeeds, so a
    // caller wanting to know "was this a lead call, and which group/item was it"
    // (e.g. to auto-advance to the next pending lead) needs it captured up front.
    const isLead = Boolean(dispositionMeta?.is_lead);
    const leadItemId = dispositionMeta?.lead_item_id || null;
    const leadAssignmentId = dispositionMeta?.lead_assignment_id || null;
    const isLeadRecall = Boolean(dispositionMeta?.is_lead_recall);
    try {
      const { payload, typedData } = buildDispositionPayload(dispositionMeta, dispositionValues, userData);
      const createResp: any = await postApiCall(API_DISPOSITION_FORM_DATA_CREATE, payload);
      if (dispositionMeta?.is_lead && dispositionMeta?.lead_item_id) {
        useCallStore.getState().clearActiveLeadContext();
      }
      dispositionSubmittedForCallSidRef.current = dispositionMeta?.call_sid || 'no-sid';
      dispositionSubmittedDuringCallRef.current = true;
      wrapUpActiveRef.current = false; dispositionPendingArmRef.current = null;
      setDispositionDeadline(null); setDispositionSecondsLeft(null);
      const completeAgentId = userData?.agentData?.id || wrapUpAgentIdRef.current || null;
      finalizeDispositionSubmit({ meta: dispositionMeta, typedData, contact: payload.contact, createResp, completeAgentId, source: 'live' });
      wrapUpAgentIdRef.current = null;
      const submittedSid = dispositionMeta?.call_sid || null;
      if (submittedSid) setPendingDispositions((prev) => prev.filter((p) => (p?.call_sid || null) !== submittedSid));
      setLastSubmittedDisposition({
        id: createResp?.disposition_form_data?.id || null,
        callSid: submittedSid,
        formName: dispositionMeta?.dispositionForm?.name || null,
        outcomes: typedData?.remarks_status || [],
        edited: false,
        meta: { ...dispositionMeta, dispositionForm: { ...(dispositionMeta?.dispositionForm || {}), follow_ups_enabled: false } },
        values: { ...dispositionValues, remarks_status: typedData?.remarks_status || [], remarks: '', follow_up_enabled: false, follow_up_date: '', follow_up_time: '' },
      });
      setDispositionMeta(null); setDispositionValues({}); setDispositionErrors({});
      setManualScreen(null);
      return { ok: true, isLead, isLeadRecall, leadItemId, leadAssignmentId };
    } catch (err) {
      console.error('Failed to submit disposition', err);
      toast.error(friendlyApiError(err) || 'Failed to submit disposition');
      return { ok: false };
    } finally { setDispositionSubmitting(false); }
  }, [dispositionMeta, dispositionSubmitting, dispositionValues, validateDisposition, userData, setManualScreen]);

  const submitPendingDisposition = useCallback(async (item, values) => {
    const meta = item?.context?.meta;
    if (!meta) { toast.error('Missing disposition context for this item'); return false; }
    if (!Array.isArray(values?.remarks_status) || values.remarks_status.length === 0) {
      toast.error('Pick an outcome to save this disposition');
      return false;
    }
    if (!userData?.agentData?.id) { toast.error('Missing agent identifiers'); return false; }
    try {
      const mergedValues = { ...(item?.context?.values || {}), ...values };
      const { payload, typedData } = buildDispositionPayload(meta, mergedValues, userData);
      const createResp: any = await postApiCall(API_DISPOSITION_FORM_DATA_CREATE, payload);
      finalizeDispositionSubmit({ meta, typedData, contact: payload.contact, createResp, completeAgentId: userData?.agentData?.id || null, source: 'pending' });
      setPendingDispositions((prev) => prev.filter((p) => p?.id !== item?.id));
      return true;
    } catch (err) {
      console.error('Failed to submit pending disposition', err);
      toast.error(friendlyApiError(err) || 'Failed to submit disposition');
      return false;
    }
  }, [userData]);

  const startEditSubmitted = useCallback(() => {
    const saved = lastSubmittedDisposition;
    if (!saved?.id || !saved?.meta || dispositionMeta) return;
    setDispositionErrors({});
    setDispositionValues({ ...(saved.values || {}) });
    setEditingSubmitted(true);
  }, [lastSubmittedDisposition, dispositionMeta]);

  const cancelEditSubmitted = useCallback(() => {
    setEditingSubmitted(false);
    setDispositionValues({}); setDispositionErrors({});
  }, []);

  const saveEditSubmitted = useCallback(async () => {
    const saved = lastSubmittedDisposition;
    if (!editingSubmitted || !saved?.id || dispositionSubmitting) return false;
    if (!validateDisposition(saved.meta)) return false;
    setDispositionSubmitting(true);
    try {
      const { payload, typedData } = buildDispositionPayload(saved.meta, dispositionValues, userData);
      await patchApiCall(API_DISPOSITION_FORM_DATA_UPDATE.replace(':id', String(saved.id)), { data: payload.data, contact: payload.contact });
      const outcomes = typedData?.remarks_status || [];
      setLastSubmittedDisposition({ ...saved, outcomes, edited: true, values: { ...dispositionValues, remarks_status: outcomes, remarks: '' } });
      setEditingSubmitted(false);
      setDispositionValues({}); setDispositionErrors({});
      trackActivity('disposition_edit', { call_sid: saved.callSid || null, disposition_form_data_id: saved.id, disposition_form_id: saved.meta?.disposition_form_id || null, remarks_status: outcomes, source: 'live' });
      toast.success('Disposition updated');
      return true;
    } catch (err) {
      console.error('Failed to update disposition', err);
      toast.error(friendlyApiError(err) || 'Failed to update disposition');
      return false;
    } finally { setDispositionSubmitting(false); }
  }, [lastSubmittedDisposition, editingSubmitted, dispositionSubmitting, dispositionValues, validateDisposition, userData]);

  useEffect(() => { setStoreDispositionSubmitting(dispositionSubmitting); },[dispositionSubmitting, setStoreDispositionSubmitting]);
  useEffect(() => () => setStoreDispositionSubmitting(false), [setStoreDispositionSubmitting]);
  useEffect(() => { setStoreDispositionActive(Boolean(dispositionMeta)); }, [dispositionMeta, setStoreDispositionActive]);
  useEffect(() => () => setStoreDispositionActive(false), [setStoreDispositionActive]);
  useEffect(() => { setStoreDispositionOpen(screen === 'disposition'); }, [screen, setStoreDispositionOpen]);
  useEffect(() => () => setStoreDispositionOpen(false), [setStoreDispositionOpen]);

  return {
    dispositionSubmitting, dispositionValues, setDispositionValues, dispositionMeta,
    dispositionErrors, setDispositionErrors,
    dispositionSecondsLeft, pendingDispositions,
    submitDisposition, submitPendingDisposition,
    lastSubmittedDisposition,
    editingSubmitted, startEditSubmitted, cancelEditSubmitted, saveEditSubmitted,
  };
}
